import * as signalR from '@microsoft/signalr'

const BASE_URL = process.env.REACT_APP_API_URL ?? ''

let connection: signalR.HubConnection | null = null
let lastToken:  string | null = null   // invoke 자동 reconnect용

// ── 연결 생성 / 반환 ──────────────────────────────────────
export function getConnection(token: string): signalR.HubConnection {
  lastToken = token
  if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
    return connection
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${BASE_URL}/Hub/Room?access_token=${encodeURIComponent(token)}`)
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  return connection
}

// ── 연결 시작 ────────────────────────────────────────────
export async function startConnection(token: string): Promise<void> {
  lastToken = token
  const conn = getConnection(token)
  if (conn.state === signalR.HubConnectionState.Disconnected) {
    await conn.start()
    // SEQ=10222 fix: 페이지(Lobby·Room) 핸들러 미등록 상황(PlayRummikub 진입 등)에서
    //   서버 OnConnected가 RoomDetail 자동 broadcast 보낼 때 'No client method' warning 차단.
    //   Lobby·Room의 conn.on('RoomDetail', ...) 등록과 공존 (SignalR 다중 핸들러).
    conn.on('RoomDetail', () => { /* noop — Lobby·Room에서 별도 핸들러 처리 */ })
  }
}

// ── 연결 보장 (invoke/on 호출 전 안전망) ──────────────────
async function ensureConnected(): Promise<signalR.HubConnection> {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    if (!lastToken) throw new Error('Hub 인증 토큰 없음 — 재로그인 필요')
    await startConnection(lastToken)
  }
  return connection!
}

// ── 연결 종료 ────────────────────────────────────────────
export async function stopConnection(): Promise<void> {
  if (connection) {
    await connection.stop()
    connection = null
  }
}

// ── Hub 메서드 invoke wrappers (클라 → 서버) ─────────────
// 자동 reconnect — connection 끊어진 상태에서 호출 시 startConnection 후 invoke
export async function invoke(method: string, ...args: unknown[]): Promise<unknown> {
  const conn = await ensureConnected()
  return conn.invoke(method, ...args)
}

export const roomCreate = (
  gameType: string,
  optionsJson: string | null,
  isPrivate: boolean,
  password: string | null,
  maxSeats: number,
) => invoke('RoomCreate', gameType, optionsJson, isPrivate, password, maxSeats)

export const roomJoin = (roomCode: string, password: string | null) =>
  invoke('RoomJoin', roomCode, password)

export const roomLeave = (roomCode: string) =>
  invoke('RoomLeave', roomCode)

export const sendChat = (roomCode: string, message: string) =>
  invoke('SendChat', roomCode, message)

export const hostTransfer = (roomCode: string, targetUserCode: number) =>
  invoke('HostTransfer', roomCode, targetUserCode)

export const optionsUpdate = (roomCode: string, optionsJson: string) =>
  invoke('OptionsUpdate', roomCode, optionsJson)

export const startGame = (roomCode: string) =>
  invoke('StartGame', roomCode)

// 같은 방에서 새 게임 재시작 (게임 종료 POST_GAME 후 멤버 누구나) — SEQ=10228 B1 (기존 SEQ=10072 방장만에서 완화)
export const restartGame = (roomCode: string) =>
  invoke('RestartGame', roomCode)

// 좌석 타입 이동 (옵저버↔플레이석, 비진행 중에만) — SEQ=10072 STEP B
export const changeSeatType = (roomCode: string, targetType: 'PLAYER' | 'OBSERVER') =>
  invoke('ChangeSeatType', roomCode, targetType)

// ── 친구 Hub invoke (P-Track 0) ───────────────────────────
export const friendRequest = (targetUserCode: number) =>
  invoke('FriendRequestHub', targetUserCode)

export const friendRespond = (requesterUserCode: number, action: 'ACCEPT' | 'REJECT') =>
  invoke('FriendRespondHub', requesterUserCode, action)

// ── 이벤트 리스너 등록 헬퍼 (서버 → 클라) ──────────────────
type HubCallback = (...args: unknown[]) => void

export function on(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.on(event, callback)
}

export function off(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.off(event, callback)
}

// ── 연결 상태 ─────────────────────────────────────────────
export function isConnected(): boolean {
  return connection?.state === signalR.HubConnectionState.Connected
}

export function getState(): signalR.HubConnectionState | null {
  return connection?.state ?? null
}
